<template>
  <section class="home-section" aria-labelledby="trending-title">
    <div class="section-header">
      <h2 id="trending-title">Trending</h2>
      <v-btn
        class="section-header__action"
        density="compact"
        to="/trending"
        variant="text"
      >
        See all
        <v-icon end>mdi-arrow-right</v-icon>
      </v-btn>
    </div>

    <div v-if="articleState.isLoading" class="card-skeletons">
      <v-skeleton-loader
        class="card-skeleton card-skeleton--featured"
        type="image, article"
      />
      <v-skeleton-loader
        v-for="i in 3"
        :key="i"
        class="card-skeleton"
        type="image, article"
      />
    </div>

    <v-sheet
      v-else-if="!articlesTrending.length"
      class="home-empty"
      color="surface"
      elevation="0"
    >
      <v-icon color="accent" size="32">mdi-trending-up</v-icon>
      <p>Trending articles will appear here soon.</p>
    </v-sheet>

    <template v-else>
      <ArticleCard :article="articlesTrending[0]" featured />

      <v-slide-group
        v-if="articlesTrending.length > 1"
        class="home-carousel"
        show-arrows
      >
        <v-slide-group-item
          v-for="article in articlesTrending.slice(1, 15)"
          :key="article.id"
        >
          <div class="home-carousel__item">
            <ArticleCard :article="article" compact />
          </div>
        </v-slide-group-item>
      </v-slide-group>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";

import ArticleCard from "@/components/ArticleCard.vue";
import { articleState } from "@/store/articlesStore";

const articlesTrending = computed(() =>
  [...articleState.articlesAll]
    .filter((article) => article.type !== "journal")
    .map((article) => ({
      ...article,
      title: article.title ?? "",
      description: article.description ?? "",
      type: article.type ?? "",
      source: article.source ?? article.source_name ?? article.name ?? "",
      views: article.views ?? 0,
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 40)
);
</script>

<style scoped>
.home-section {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-header h2 {
  margin: 0;
  color: rgb(var(--v-theme-on-surface));
  font-size: clamp(1.55rem, 3vw, 2rem);
  font-weight: 850;
  line-height: 1.2;
  text-transform: uppercase;
}

.section-header__action {
  flex: 0 0 auto;
  border-radius: 8px;
  font-weight: 750;
}

.home-carousel {
  width: 100%;
  margin-top: 16px;
}

.home-carousel__item {
  width: 318px;
  height: 100%;
  padding: 4px 8px 12px;
}

.card-skeletons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.card-skeleton {
  min-height: 420px;
  border-radius: 8px;
}

.card-skeleton--featured {
  grid-column: 1 / -1;
}

.home-empty {
  display: grid;
  gap: 12px;
  place-items: center;
  min-height: 220px;
  padding: 32px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 8px;
  color: rgba(var(--v-theme-on-surface), 0.68);
  text-align: center;
}

.home-empty p {
  margin: 0;
}

:deep(.home-carousel .v-slide-group__prev),
:deep(.home-carousel .v-slide-group__next) {
  min-width: 44px;
  color: rgb(var(--v-theme-accent));
}

@media (max-width: 700px) {
  .home-carousel__item {
    width: min(82vw, 310px);
    padding-left: 4px;
    padding-right: 8px;
  }

  .card-skeletons {
    grid-template-columns: 1fr;
  }

  :deep(.home-carousel .v-slide-group__prev),
  :deep(.home-carousel .v-slide-group__next) {
    display: none;
  }
}
</style>
